import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useTheme } from '../../../theme';
import { useConfig } from '../../../context';
import { useRenderers } from '../../../renderProps';
import { ConfirmOptionVariant } from '../../types';
import { Tooltip } from '../../../BaseComponents';
import { CdnIcon, CdnIconNames } from '@shared/ui';
import { isExistI18nTranslate } from '../../../utils';
import { SafeHtml } from '@shared/ui';

interface Props {
	title?: React.ReactNode;
	description?: string | React.ReactNode;
	showConfirmedIcon?: boolean;
	variant: ConfirmOptionVariant;
	isOld?: boolean;
	isDisabled?: boolean;
}

const DescriptionBlock: React.FC<Props> = ({ description, title, showConfirmedIcon, variant, isOld, isDisabled }) => {
	const { t, i18n } = useTranslation('FareLock');
	const { DescriptionBlock: css } = useTheme('FareLock');

	const {
		global: { companyInfo }
	} = useConfig();

	const { getConfirmOptionIcon } = useRenderers();

	const existWarningDescription = i18n.exists('WarningDescription', { ns: 'FareLock' });

	const getIcon = (): CdnIconNames => {
		switch (variant) {
			case 'FareLock':
				return 'Info/FareLock';

			case 'Refund':
				return 'Services/Refundable';

			case 'Upgrade':
				return 'Arrow/Upward';

			case 'Note':
				return 'Info/Lock';

			default:
				return null;
		}
	};

	const renderDetails = ({
		link,
		action,
		text,
		title
	}: {
		link?: string;
		action?: () => void;
		text: string;
		title?: string;
	}) => {
		return link ? (
			<a
				href={link ?? '#'}
				onClick={event => {
					if (action) {
						event.preventDefault();
						action();
					}
				}}
				className={css.rules}
				target="_blank"
			>
				<CdnIcon icon="Info/Details" />
				<SafeHtml tag="span" html={text} />
			</a>
		) : (
			title && (
				<Tooltip title={title} disableFocusListener={isDisabled} disableHoverListener={isDisabled}>
					<span className={css.rules}>
						<CdnIcon icon="Info/Details" /> {text}
					</span>
				</Tooltip>
			)
		);
	};

	const renderFareLock = () => {
		return existWarningDescription ? (
			<SafeHtml tag="div" className={css.warning} html={t('WarningDescription')} />
		) : (
			companyInfo.farelockURL &&
				isExistI18nTranslate('Rules of service provision', { ns: 'FareLock' }) &&
				renderDetails({
					link: companyInfo.farelockURL,
					text: t('Rules of service provision')
				})
		);
	};

	const icon = getIcon();

	return (
		<div
			className={cn(css.root, {
				[css.root_old]: isOld,
				[css.root_disabled]: isDisabled,
				[css.root_refund]: variant === 'Refund',
				[css.root_fareLock]: variant === 'FareLock',
				[css.root_upgrade]: variant === 'Upgrade',
				[css.root_note]: variant === 'Note'
			})}
		>
			{icon && (
				<div className={css.iconWrapper}>
					<CdnIcon className={css.icon} icon={getConfirmOptionIcon?.({ variant }) ?? getIcon()} />
				</div>
			)}
			<div className={css.info}>
				<div className={css.title}>
					{showConfirmedIcon && <CdnIcon className={css.confirmedIcon} icon="Info/Choosed" />}
					{title}
				</div>
				<div className={css.description}>
					<div className={css.description__text}>{description}</div>

					{variant === 'FareLock' && renderFareLock()}

					{variant === 'Refund' &&
						renderDetails({
							text: t('Additional information'),
							title: t(
								'The cost of the refund service will be added to the fare and fees. The total amount will increase by the specified cost.'
							)
						})}

					{variant === 'Upgrade' &&
						companyInfo.upgradeOptionURL &&
						renderDetails({
							link: companyInfo.upgradeOptionURL,
							text: t('UpgradeFlightClass:More details')
						})}
				</div>
			</div>
		</div>
	);
};

export default DescriptionBlock;
